import { GetStaticPaths, GetStaticPropsContext } from 'next'

import Main from '@/layouts/main'
import {
  getConsultancyDetailPageData,
  getConsultancyDetailSlugs,
  getRvdbNavigationData,
} from '@/services/contentful/api'
import { getPaths } from '@/lib/paths'
import { ConsultancyDetail } from '@/interfaces/schema'
import PageProps from '@/interfaces/page'
import { getSlugs, stringifySlug } from '@/lib/slugs'
import { BannerMedium } from '@/sections/repeatable'
import OrganizationCardsSection from '@/sections/rvdb/organization-cards'
import StoriesCardsSection from '@/sections/rvdb/stories-cards'
import DetailCTAFormSection from '@/sections/rvdb/detail-cta-form'
import ClientsSection from '@/sections/repeatable/clients'
import ReviewSection from '@/sections/repeatable/review'
import ContentSmallSection from '@/sections/repeatable/content-small'

const ConsultancyDetailpage = ({ data }: PageProps<ConsultancyDetail>) => {
  const {
    bannerTitle,
    bannerImage,
    displayIntroductionSection,
    introductionSectionId,
    introductionContent,
    displayContentSection,
    contentSectionId,
    contentIcon,
    contentTitle,
    contentBody,
    displayFirstConsultancyCardsSection,
    firstConsultancyCardsSectionId,
    firstConsultancyCardsSectionIcon,
    firstConsultancyCardsSectionTitle,
    firstConsultancyCardsListCollection,
    displaySecondConsultancyCardsSection,
    secondConsultancyCardsSectionId,
    secondConsultancyCardsSectionIcon,
    secondConsultancyCardsSectionTitle,
    secondConsultancyCardsListCollection,
    displayStoriesCardsSection,
    storiesCardsSectionId,
    storiesCardsSectionTitle,
    storiesCardsSectionIcon,
    storiesCardsListCollection,
    storiesCardsSectionMoreButtonText,
    storiesCardsSectionMoreButtonPath,
    displayCallToActionSection,
    callToActionSectionId,
    callToActionSectionIcon,
    callToActionSectionTitle,
    callToActionSectionBody,
    contactFormCode,
    displayClientsSection,
    clientsSectionId,
    clientsSectionIcon,
    clientsSectionTitle,
    clientsList,
    displayReviewSection,
    reviewSectionId,
    reviewSectionIcon,
    reviewSectionTitle,
    testimonialsCollection,
  } = data

  return (
    <Main>
      <BannerMedium bannerTitle={bannerTitle} bannerImage={bannerImage} />
      {displayIntroductionSection && (
        <ContentSmallSection
          contentBody={introductionContent}
          id={introductionSectionId}
        />
      )}
      {displayContentSection && (
        <ContentSmallSection
          contentIcon={contentIcon}
          contentTitle={contentTitle}
          contentBody={contentBody}
          id={contentSectionId}
        />
      )}
      {displayFirstConsultancyCardsSection && (
        <OrganizationCardsSection
          isConsultancy
          icon={firstConsultancyCardsSectionIcon}
          title={firstConsultancyCardsSectionTitle}
          cardsCollection={firstConsultancyCardsListCollection}
          id={firstConsultancyCardsSectionId}
        />
      )}
      {displaySecondConsultancyCardsSection && (
        <OrganizationCardsSection
          isConsultancy
          icon={secondConsultancyCardsSectionIcon}
          title={secondConsultancyCardsSectionTitle}
          cardsCollection={secondConsultancyCardsListCollection}
          id={secondConsultancyCardsSectionId}
        />
      )}
      {displayStoriesCardsSection && (
        <StoriesCardsSection
          storiesCardsSectionTitle={storiesCardsSectionTitle}
          storiesCardsSectionIcon={storiesCardsSectionIcon}
          storiesCardsListCollection={storiesCardsListCollection}
          storiesCardsSectionMoreButtonText={storiesCardsSectionMoreButtonText}
          storiesCardsSectionMoreButtonPath={storiesCardsSectionMoreButtonPath}
          id={storiesCardsSectionId}
        />
      )}
      {displayCallToActionSection && (
        <DetailCTAFormSection
          callToActionSectionIcon={callToActionSectionIcon}
          callToActionSectionTitle={callToActionSectionTitle}
          callToActionSectionBody={callToActionSectionBody}
          contactFormCode={contactFormCode}
          id={callToActionSectionId}
        />
      )}
      {displayClientsSection && (
        <ClientsSection
          clientsSectionIcon={clientsSectionIcon}
          clientsSectionTitle={clientsSectionTitle}
          clientsList={clientsList}
          id={clientsSectionId}
        />
      )}
      {displayReviewSection && (
        <ReviewSection
          reviewSectionIcon={reviewSectionIcon}
          reviewSectionTitle={reviewSectionTitle}
          testimonialsCollection={testimonialsCollection}
          id={reviewSectionId}
        />
      )}
    </Main>
  )
}

export async function getStaticProps({
  params,
  preview,
}: GetStaticPropsContext) {
  const isPreview: boolean = !!preview ?? false
  const slug = stringifySlug(params)

  const { data: pageData, error: pageError } =
    await getConsultancyDetailPageData(slug, isPreview)

  const { data: navData, error: navError } = await getRvdbNavigationData(
    isPreview
  )

  if (!pageData?.items.length) return { notFound: true }
  return {
    props: {
      data: { navData, pageData: pageData?.items[0] },
      error: { pageError, navError },
    },
    revalidate: 60,
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const isPreview = false
  const { data } = await getConsultancyDetailSlugs(isPreview)
  const slugs = getSlugs(data)
  const paths = getPaths(slugs)

  return {
    paths,
    fallback: 'blocking',
  }
}

export default ConsultancyDetailpage
